import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { numberField_updateWorkItemFieldsActionRenderer$key } from './__generated__/numberField_updateWorkItemFieldsActionRenderer.graphql';

const messages = defineMessages({
	setAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.jira-number-field.setStorypoints',
		defaultMessage: 'Set {fieldName} to {fieldValue}',
		description: 'Label for the set story points in the jira number field',
	},
	clearAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.jira-number-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the jira number field',
	},
});

export type Props = {
	queryRef: numberField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const NumberField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment numberField_updateWorkItemFieldsActionRenderer on JiraNumberField {
				fieldId
				name
				number
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === action?.data?.fieldKey;

	const isUpdatingAction = Boolean(action?.data?.fieldValue);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				fieldName={data?.name}
				current={data?.number ? String(data.number) : undefined}
				suggested={action?.data?.fieldValue ? String(action.data.fieldValue) : undefined}
			/>
		),
		[action.data.fieldValue, action.data?.reasoning, data?.name, data?.number],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!data?.fieldId || !data?.name || data?.fieldId !== action.data.fieldKey) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.jira-number-field"
		>
			{isUpdatingAction
				? formatMessage(messages.setAction, {
						fieldName: data?.name?.toLowerCase(),
						fieldValue: action?.data?.fieldValue ? String(action.data.fieldValue) : undefined,
					})
				: formatMessage(messages.clearAction, {
						fieldName: data?.name?.toLowerCase(),
					})}
		</Text>
	);
};
